import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { waLink, WhatsAppIcon } from "@/lib/whatsapp";
import Reveal from "@/components/Reveal";
import Marquee from "@/components/Marquee";

const BRAND_CATS = [
  { key: "honda", label: "Honda" },
  { key: "proton", label: "Proton" },
  { key: "jaecoo", label: "Jaecoo" },
  { key: "omoda", label: "Omoda" },
];

export default function Offers() {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [brand, setBrand] = useState("");
  const listRef = useRef(null);

  useEffect(() => {
    document.title = "This Month's Offers — AutoWorld";
    api.offers().then((d) => { setOffers(d); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const filtered = useMemo(
    () => (brand ? offers.filter((o) => o.brand === brand) : []),
    [offers, brand]
  );
  const countFor = (k) => offers.filter((o) => o.brand === k).length;
  const activeLabel = BRAND_CATS.find((c) => c.key === brand)?.label || "";

  const pickBrand = (k) => {
    setBrand(k === brand ? "" : k);
    if (k !== brand) {
      setTimeout(() => listRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 120);
    }
  };

  return (
    <div className="pt-16 md:pt-20" data-testid="offers-page">
      <section className="border-b border-black/10">
        <div className="mx-auto max-w-[1400px] px-5 py-14 md:px-10 md:py-20">
          <Reveal>
            <div className="font-mono text-[10px] uppercase tracking-[0.35em] text-neutral-500">October 2026 · Updated monthly</div>
            <h1 className="mt-4 font-display font-extrabold uppercase tracking-tighter" style={{ fontSize: "clamp(2rem, 8.4vw, 4.5rem)", lineHeight: 1.02 }}>
              This Month's<br /><span className="text-stroke">Offers</span>
            </h1>
            <p className="mt-4 max-w-md text-neutral-600">
              Direct-dealer deals, no fine-print games. Pick a brand to reveal this month's list.
            </p>
          </Reveal>
        </div>
      </section>

      <Marquee items={["Limited Time", "Direct Dealer Deals", "No Agent Markup", "Claim via WhatsApp"]} />

      {/* BRAND CATEGORIES */}
      <section className="mx-auto max-w-[1400px] px-5 py-12 md:px-10 md:py-16" data-testid="offers-categories">
        <Reveal>
          <div className="font-mono text-[10px] uppercase tracking-[0.35em] text-neutral-500">Choose a brand</div>
        </Reveal>
        <div className="mt-6">
          {BRAND_CATS.map((c, i) => {
            const on = brand === c.key;
            return (
              <Reveal key={c.key} delay={i * 0.06} className="min-w-0">
                <button
                  data-testid={`offer-cat-${c.key}`}
                  onClick={() => pickBrand(c.key)}
                  className={`group flex w-full items-center justify-between gap-4 border-t border-black/10 px-4 py-7 text-left transition-colors last:border-b md:px-6 md:py-9 ${
                    on ? "bg-black text-white" : "hover:bg-neutral-50"
                  }`}
                >
                  <div className="min-w-0">
                    <div className={`font-mono text-[9px] uppercase tracking-[0.3em] ${on ? "text-neutral-400" : "text-neutral-400"}`}>
                      {loading ? "…" : `${countFor(c.key)} offers this month`}
                    </div>
                    <div className="mt-1 font-display font-extrabold uppercase tracking-tighter" style={{ fontSize: "clamp(1.9rem, 6vw, 3.8rem)", lineHeight: 1.02 }}>
                      {c.label}
                    </div>
                  </div>
                  <span className={`shrink-0 font-display text-2xl transition-transform duration-300 md:text-3xl ${on ? "rotate-90" : "group-hover:translate-x-2"}`}>
                    →
                  </span>
                </button>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* OFFER LIST (revealed on brand pick) */}
      {brand && (
        <section ref={listRef} className="mx-auto max-w-[1400px] scroll-mt-24 px-5 pb-16 md:px-10 md:pb-24" data-testid="offers-list">
          {/* WhatsApp highlight note */}
          <Reveal>
            <div
              data-testid="offers-wa-note"
              className="flex flex-col items-start justify-between gap-5 bg-black p-6 text-white md:flex-row md:items-center md:p-9"
            >
              <div className="min-w-0">
                <div className="font-mono text-[9px] uppercase tracking-[0.3em] text-neutral-400">Before you browse</div>
                <p className="mt-2 break-words font-display font-extrabold uppercase tracking-tight" style={{ fontSize: "clamp(1.2rem, 3vw, 1.8rem)", lineHeight: 1.1 }}>
                  Better offers can be provided through WhatsApp
                </p>
                <p className="mt-2 text-sm text-neutral-400">
                  Today's best direct deal may not even be listed — message us and ask.
                </p>
              </div>
              <a
                href={waLink("Hi AutoWorld! What better offers can you give me this month?")}
                target="_blank"
                rel="noreferrer"
                data-testid="offers-wa-note-cta"
                className="flex shrink-0 items-center gap-2 bg-white px-6 py-4 font-mono text-[10px] uppercase tracking-[0.25em] text-black transition-colors hover:bg-neutral-200"
              >
                <WhatsAppIcon className="h-4 w-4" /> Ask on WhatsApp
              </a>
            </div>
          </Reveal>

          <div className="mt-8 flex items-center justify-between">
            <h2 className="font-display text-2xl font-extrabold uppercase tracking-tighter md:text-3xl" data-testid="offers-list-title">
              {activeLabel} Offers
            </h2>
            <button
              data-testid="offers-back"
              onClick={() => setBrand("")}
              className="font-mono text-[10px] uppercase tracking-[0.25em] underline underline-offset-4 hover:opacity-60"
            >
              ← All brands
            </button>
          </div>

          <div className="mt-6 grid grid-cols-1 gap-5 md:grid-cols-2 md:gap-6" data-testid="offers-grid">
            {filtered.map((o, i) => (
              <Reveal key={o.id} delay={(i % 2) * 0.08} className="min-w-0">
                <article
                  data-testid={`offer-card-${o.id}`}
                  className={`group flex h-full min-w-0 flex-col overflow-hidden border p-6 transition-colors md:p-10 ${
                    i % 2 === 1 ? "border-black bg-black text-white" : "border-black/10 bg-white hover:border-black"
                  }`}
                >
                  <div className={`flex flex-wrap items-center justify-between gap-x-3 gap-y-1 font-mono text-[9px] uppercase tracking-[0.3em] ${i % 2 === 1 ? "text-neutral-400" : "text-neutral-500"}`}>
                    <span>{activeLabel}</span>
                    <span>Until {o.valid_until}</span>
                  </div>
                  <h2
                    className="mt-4 break-words font-display font-extrabold uppercase leading-[1.08] tracking-tight"
                    style={{ fontSize: "clamp(1.35rem, 3.2vw, 2.4rem)" }}
                    data-testid={`offer-highlight-${o.id}`}
                  >
                    {o.highlight}
                  </h2>
                  <h3 className="mt-2 font-display text-lg font-bold">{o.title}</h3>
                  {o.points?.length > 0 ? (
                    <ul className="mt-3 flex-1 space-y-2" data-testid={`offer-points-${o.id}`}>
                      {o.points.map((p) => (
                        <li key={p} className={`flex gap-3 text-sm leading-relaxed ${i % 2 === 1 ? "text-neutral-300" : "text-neutral-600"}`}>
                          <span className={`mt-[7px] h-1.5 w-1.5 shrink-0 ${i % 2 === 1 ? "bg-white" : "bg-black"}`} />
                          <span className="break-words">{p}</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className={`mt-3 flex-1 break-words text-sm leading-relaxed ${i % 2 === 1 ? "text-neutral-400" : "text-neutral-600"}`}>
                      {o.description}
                    </p>
                  )}
                  {o.terms && (
                    <p className={`mt-4 font-mono text-[9px] uppercase tracking-[0.15em] ${i % 2 === 1 ? "text-neutral-500" : "text-neutral-400"}`}>
                      {o.terms}
                    </p>
                  )}
                  <a
                    href={waLink(`Hi AutoWorld! I want to claim: ${o.title} (${o.highlight}).`)}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`offer-claim-${o.id}`}
                    className={`mt-6 flex items-center justify-center gap-2 py-4 font-mono text-[11px] uppercase tracking-[0.25em] transition-colors ${
                      i % 2 === 1 ? "bg-white text-black hover:bg-neutral-200" : "bg-black text-white hover:bg-neutral-800"
                    }`}
                  >
                    <WhatsAppIcon className="h-4 w-4" /> Claim on WhatsApp
                  </a>
                </article>
              </Reveal>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
